import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../firebase/config';
import { theme } from '../styles/GlobalStyles';
import Input from '../components/Input';
import Button from '../components/Button';
import { TopWaves, BottomWaves } from '../components/WaveShapes';
import { ArrowLeftIcon } from '../components/Icons';

const SignUpContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  position: relative;
  overflow: hidden;
`;

const SignUpCard = styled.div`
  background-color: ${theme.colors.white};
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: ${theme.spacing.xl};
  width: 100%;
  max-width: 400px;
  z-index: 10;
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    box-shadow: none;
    padding: ${theme.spacing.lg} ${theme.spacing.sm};
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #7f8c8d;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: 0.9rem;
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.xs};
  transition: all 0.2s ease;
  
  &:hover {
    color: #2c3e50;
  }
`;

const SignUpTitle = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
`;

const LogoImage = styled.img`
  height: auto;
  max-width: 220px;
  margin: 0 auto;
  display: block;
`;

const WelcomeText = styled.h2`
  color: ${theme.colors.deepDark};
  margin: ${theme.spacing.md} 0;
  font-size: 1.8rem;
  font-weight: 600;
`;

const SubTitle = styled.p`
  color: ${theme.colors.lightText};
  font-size: 1rem;
  margin-bottom: ${theme.spacing.md};
`;

const SignUpForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.sm};
  }
`;

const SignUpFooter = styled.div`
  margin-top: ${theme.spacing.lg};
  text-align: center;
  font-size: 0.875rem;
  color: ${theme.colors.lightText};
  
  a {
    color: ${theme.colors.primary};
    font-weight: 500;
    text-decoration: none;
    margin-left: 5px;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(228, 139, 136, 0.1);
  border: 1px solid ${theme.colors.error};
  border-radius: 12px;
  color: ${theme.colors.error};
  padding: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  font-size: 0.875rem;
`;

const CreateButton = styled(Button)`
  margin-top: ${theme.spacing.sm};
  height: 48px;
  background: linear-gradient(to right, ${theme.colors.secondary}, ${theme.colors.tertiary});
`;

const SocialOptions = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: ${theme.spacing.md} 0;
  color: ${theme.colors.lightText};
  font-size: 0.875rem;
  
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.1);
  }
  
  &::before {
    margin-right: ${theme.spacing.sm};
  }
  
  &::after {
    margin-left: ${theme.spacing.sm};
  }
`;

const SignUp: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= parseInt(theme.breakpoints.mobile));
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name
      await updateProfile(userCredential.user, {
        displayName: name
      });
      
      navigate('/app');
    } catch (err: any) {
      console.error("Signup error:", err);
      setError(err.message || 'Failed to create an account. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <SignUpContainer>
      {!isMobile && <TopWaves />}
      {!isMobile && <BottomWaves />}
      
      <SignUpCard>
        <BackButton onClick={() => navigate('/')}>
          <ArrowLeftIcon size={20} />
        </BackButton>
        <SignUpTitle>
          <LogoImage src="/images/MMNHeader.png" alt="My NeuroNest" />
          <WelcomeText>Create account</WelcomeText>
          <SubTitle>Start your journey with us today</SubTitle>
        </SignUpTitle>
        
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        
        <SignUpForm onSubmit={handleSignUp} aria-label="Sign up form" noValidate>
          <Input
            id="name"
            name="name"
            label="Name"
            type="text"
            placeholder="Enter your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoComplete="name"
          />
          
          <Input
            id="email"
            name="email"
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoComplete="email"
          />
          
          <Input
            id="password"
            name="password"
            label="Password"
            type="password"
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
          />
          
          <Input
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            placeholder="Confirm your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
          />
          
          <CreateButton 
            type="submit" 
            fullWidth 
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </CreateButton>
        </SignUpForm>
        
        <SignUpFooter>
          Already have an account?<Link to="/login">Sign In</Link>
        </SignUpFooter>
      </SignUpCard>
    </SignUpContainer>
  );
};

export default SignUp; 